import {
  Box,
  Divider,
  Stack,
  TextField,
  Typography,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Autocomplete,
  Avatar,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import RightSidebar from "components/base/RightDrawer";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import PeopleIcon from "@mui/icons-material/People";
import React, { useEffect, useState } from "react";
import { emailValidation } from "utils/validation.util";
import { toast } from "react-toastify";
import { useParams, Link } from "react-router-dom";
import PublicConfirm from "./PublicConfirm";
import { useSelector } from "react-redux";
import RemoveModal from "./RemovePopup";
import PrivateConfirm from "./PrivateConfirmPopup";
import PublicCollectionDetailCard from "./PublicCollectionDetailCard";
import UserListItem from "features/Settings/components/UserListItem";
import { useDistributionLists } from "features/Settings/hooks/distribution-lists.hook";
import AccessibleIconButton from "components/base/AccessibleIconButton";
import { formatDistributionListHandle } from "utils/distribution-list.util";

const AccessSidebar = ({
  accessPopup,
  setAccessPopup,
  getUsersList,
  shareCollectionFolder,
  removeSharedUser,
  collection,
  length,
  makeCollectionPublic,
  handleRemoveAllSharedUsers,
}) => {
  const params = useParams();
  const theme = useTheme();
  const navbarBackground = theme.palette?.navbar?.main;
  const [view, setView] = useState(
    collection.isPublic_ysn ? "public" : "private"
  );
  const [users, setUsers] = useState([]);
  const [confirmPopup, setConfirmPopup] = useState(false);
  const [email, setEmail] = useState("");
  const [remove, setRemove] = useState(false);
  const [removeEmail, setRemoveEmail] = useState([]);
  const [privatePopup, setPrivatePopup] = useState(false);
  const [showDistributionLists, setShowDistributionLists] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);

  const { user } = useSelector((state) => state.auth);
  const { distributionLists, getDistributionLists } = useDistributionLists();

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      if (newView === "public" && users.length > 0) {
        toast.error(
          "A shared collection cannot be made public. Remove all the users first to make it public."
        );
        return;
      } else if (newView === "public") {
        setConfirmPopup(true);
      } else if (newView === "private") {
        setPrivatePopup(true);
      }
    }
  };

  const setUsersList = async () => {
    setUsersLoading(true);
    try {
      const data = await getUsersList();
      setUsers(data);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleEmailChange = (value) => {
    setEmail(value);

    const items = value.split(",");
    const currentItem = items[items.length - 1].trim();

    if (currentItem.startsWith("@")) {
      const query = currentItem.substring(1);
      setSearchQuery(query);
      setShowDistributionLists(true);
    } else {
      setShowDistributionLists(false);
      setSearchQuery("");
    }
  };

  const filteredDistributionLists = (distributionLists || []).filter((list) => {
    if (!list?.name && !list?.slug) return false;
    const searchLower = searchQuery.toLowerCase();
    const nameLower = (list?.name || "").toLowerCase();
    const slugLower = (list?.slug || "").toLowerCase();
    return nameLower.includes(searchLower) || slugLower.includes(searchLower);
  });

  const handleAddEmail = async () => {
    try {
      if (email === "") {
        toast.error("Please enter email addresses or distribution lists.");
        return;
      }

      setIsAdding(true);

      const items = email
        .split(",")
        .map((item) => item.trim())
        .filter((item) => item);
      const distributionListItems = items.filter((item) =>
        item.startsWith("@")
      );
      const emailItems = items.filter((item) => !item.startsWith("@"));

      if (distributionListItems.length > 1) {
        toast.error("You can only add one distribution list at a time.");
        setIsAdding(false);
        return;
      }

      if (distributionListItems.length > 0 && emailItems.length > 0) {
        toast.error(
          "Cannot add distribution list and emails together. Please add them separately."
        );
        setIsAdding(false);
        return;
      }

      let hasShared = false;

      if (distributionListItems.length > 0) {
        const item = distributionListItems[0];

        if (!distributionLists || distributionLists.length === 0) {
          toast.error(
            "Distribution lists are not loaded yet. Please try again."
          );
        } else {
          const distList = distributionLists.find((list) => list.slug === item);

          if (distList) {
            const isDuplicate = users.some(
              (u) =>
                (u.sharedWithTypeId === 2 || u.isDistributionList) &&
                (u.generatedDistributionListName === item || u.email === item)
            );
            if (isDuplicate) {
              toast.error(
                `Distribution list "${distList.name}" is already added.`
              );
            } else {
              await shareCollectionFolder({
                CollectionId: Number(params.id),
                SharedWithTypeId: 2,
                DistributionListId: distList.id,
                UniqueDistributionListName: distList.slug,
              });
              hasShared = true;
            }
          } else {
            toast.error(`Distribution list "${item}" not found.`);
          }
        }
      }

      if (emailItems.length > 0) {
        const validEmails = [];
        const invalidEmails = [];

        for (const item of emailItems) {
          if (!emailValidation(item)) {
            invalidEmails.push(item);
            continue;
          }

          if (item === user.email) {
            toast.error(
              "You cannot share the collection with your own email address."
            );
            continue;
          }

          const isDuplicate = users.some(
            (u) => u.sharedWithTypeId === 1 && u.email === item
          );
          if (isDuplicate) {
            toast.error(`Email "${item}" is already added to this collection.`);
            continue;
          }

          validEmails.push(item);
        }

        if (invalidEmails.length > 0) {
          toast.error(
            `Invalid email address${
              invalidEmails.length > 1 ? "es" : ""
            }: ${invalidEmails.join(", ")}`
          );
        }

        if (validEmails.length > 0) {
          await shareCollectionFolder({
            CollectionId: Number(params.id),
            SharedWithTypeId: 1,
            Email: validEmails,
          });
          hasShared = true;
        }
      }

      setEmail("");
      setShowDistributionLists(false);
      setSearchQuery("");

      if (hasShared) {
        await setUsersList();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsAdding(false);
    }
  };

  const handleKeyDown = async (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      setShowDistributionLists(false);
      await handleAddEmail();
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      if (removeEmail === "all") {
        await handleRemoveAllSharedUsers();
      } else {
        const userToRemove = users.find(
          (u) =>
            u.email === removeEmail ||
            u.generatedDistributionListName === removeEmail
        );
        await removeSharedUser(params.id, userToRemove);
      }
      await setUsersList();
    } catch (e) {
      console.error(e);
    } finally {
      setIsDeleting(false);
      setRemove(false);
    }
  };

  useEffect(() => {
    if (accessPopup) {
      setUsersList();
      getDistributionLists();
    }
  }, [accessPopup]);

  return (
    <React.Fragment>
      <RightSidebar open={accessPopup} onClose={() => setAccessPopup(false)}>
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              backgroundColor: "secondary.main",
              alignItems: "center",
              p: 3,
            }}
          >
            <Typography
              variant="h5"
              gutterBottom
              sx={{ fontSize: "2rem", color: "gray" }}
            >
              Manage Access to "{collection.collectionName_chr}"
            </Typography>
            <AccessibleIconButton
              color="inherit"
              title="Close"
              edge="start"
              onClick={() => setAccessPopup(false)}
              sx={{ p: 0 }}
            >
              <CloseIcon sx={{ color: "#a9baca", height: "2.2rem" }} />
            </AccessibleIconButton>
          </Box>

          <Box sx={{ p: 2, mt: 2, px: 3 }}>
            <Stack>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{ color: "gray", fontWeight: "bold" }}
                >
                  Others with access
                </Typography>
                {users.length !== 0 && (
                  <Button
                    variant="outlined"
                    disabled={isDeleting || usersLoading}
                    onClick={() => {
                      setRemoveEmail("all");
                      setRemove(true);
                    }}
                  >
                    Remove All
                  </Button>
                )}
              </Stack>
              <Box sx={{ maxHeight: "400px", overflowY: "auto", px: 1 }}>
                {usersLoading &&
                  collection.isShared_ysn &&
                  users.length === 0 && (
                    <>
                      {[...Array(3)].map((_, i) => (
                        <Stack
                          key={`skeleton-${i}`}
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{ mb: 2 }}
                        >
                          <Stack direction="row" alignItems="center" gap={1}>
                            <Skeleton
                              variant="circular"
                              width={40}
                              height={40}
                            />
                            <Stack>
                              <Skeleton
                                variant="text"
                                width={180}
                                height={20}
                              />
                              <Skeleton
                                variant="text"
                                width={220}
                                height={16}
                              />
                            </Stack>
                          </Stack>
                          <Skeleton variant="rounded" width={90} height={32} />
                        </Stack>
                      ))}
                    </>
                  )}
                {(!usersLoading ||
                  !collection.isShared_ysn ||
                  users.length > 0) &&
                  users.map((user) => (
                    <UserListItem
                      key={
                        user.email ||
                        user.generatedDistributionListName ||
                        user.userId
                      }
                      user={user}
                      loading={isDeleting}
                      hideButton={
                        user.sharedWithTypeId === 2 &&
                        user.isDistributionListOwner === false
                      }
                      onRemove={(u) => {
                        setRemoveEmail(
                          u.email || u.generatedDistributionListName
                        );
                        setRemove(true);
                      }}
                    />
                  ))}
              </Box>
              {view === "public" ? (
                <Typography variant="body1" color={"#6d6d6d"}>
                  This collection cannot be shared while it's public. Switch to
                  Private before sharing access with others.
                </Typography>
              ) : (
                <Autocomplete
                  freeSolo
                  open={showDistributionLists}
                  options={filteredDistributionLists}
                  getOptionLabel={(option) =>
                    typeof option === "string" ? option : option?.name || ""
                  }
                  filterOptions={(x) => x}
                  disabled={isAdding}
                  onInputChange={(event, value, reason) => {
                    if (reason === "input" || reason === "clear") {
                      handleEmailChange(value || "");
                    }
                  }}
                  onChange={(event, value) => {
                    if (value && typeof value === "object") {
                      const items = email.split(",");
                      items[items.length - 1] = `${value.slug}`;
                      setEmail(items.join(", "));
                      setShowDistributionLists(false);
                      setSearchQuery("");
                    }
                  }}
                  onClose={() => setShowDistributionLists(false)}
                  inputValue={email}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Add emails (comma-separated) or @distribution-list"
                      onKeyDown={handleKeyDown}
                      disabled={isAdding}
                      sx={{ width: "100%" }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isAdding && (
                              <CircularProgress size={20} sx={{ mr: 1 }} />
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box
                      component="li"
                      {...props}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 1,
                        py: 0.5,
                      }}
                    >
                      <Avatar
                        sx={{ bgcolor: "primary.main", width: 32, height: 32 }}
                      >
                        <PeopleIcon sx={{ fontSize: "2rem" }} />
                      </Avatar>
                      <Stack gap={0}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {formatDistributionListHandle(option?.slug)}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "grey.600", fontSize: "1.2rem" }}
                        >
                          {option?.name || ""}
                        </Typography>
                      </Stack>
                    </Box>
                  )}
                  slotProps={{
                    popper: {
                      placement: "bottom-start",
                      style: { zIndex: 9999 },
                      modifiers: [
                        {
                          name: "offset",
                          options: {
                            offset: [0, 4],
                          },
                        },
                      ],
                    },
                  }}
                  noOptionsText="No distribution lists found"
                />
              )}

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {view !== "public" && (
                  <Typography
                    variant="body2"
                    color="grey"
                    sx={{ alignSelf: "flex-end" }}
                  >
                    Hit Enter when you're done.
                  </Typography>
                )}
                {view !== "public" && (
                  <Typography variant="body2" color="grey.600">
                    To create a distribution list, go to your{" "}
                    <Link
                      to="/profile/preferences?id=distribution-lists"
                      style={{
                        color: theme.palette.primary.main,
                        textDecoration: "none",
                      }}
                    >
                      Preferences
                    </Link>
                    .
                  </Typography>
                )}
              </Box>

              <Divider sx={{ my: 4 }} />
            </Stack>

            <Stack gap={2}>
              <Typography
                variant="h5"
                gutterBottom
                sx={{ color: "gray", fontWeight: "bold" }}
              >
                Publish settings
              </Typography>
              <Stack direction="row" gap={7} alignItems={"center"} mb={"1rem"}>
                <ToggleButtonGroup
                  disabled={users.length !== 0}
                  value={view}
                  exclusive
                  onChange={handleViewChange}
                  aria-label="Private/Public Toggle"
                  sx={{
                    "&.Mui-disabled": {
                      pointerEvents: "none", // Ensures the buttons are not clickable
                    },
                  }}
                >
                  <ToggleButton
                    value="private"
                    startIcon={view === "private" ? <CheckIcon /> : null}
                    aria-label="private"
                    sx={{
                      width: "10rem",
                      height: "5rem",
                      textTransform: "none",
                      color: "black",
                      borderRadius: 10,
                      "&.Mui-selected": {
                        backgroundColor: navbarBackground,
                        color: "#125997",
                        fontWeight: "bold",
                      },
                      "&.Mui-disabled": {
                        color: "#93aec0",
                      },
                    }}
                  >
                    {view === "private" && (
                      <CheckIcon sx={{ marginRight: "8px", width: "2rem" }} />
                    )}
                    Private
                  </ToggleButton>
                  <ToggleButton
                    value="public"
                    aria-label="public"
                    sx={{
                      width: "10rem",
                      height: "5rem",
                      textTransform: "none",
                      color: "black",
                      borderRadius: 10,
                      "&.Mui-selected": {
                        backgroundColor: navbarBackground,
                        color: "#125997",
                        fontWeight: "bold",
                      },
                      "&.Mui-disabled": {
                        color: "#93aec0",
                        backgroundColor: "#eff4f8",
                      },
                    }}
                  >
                    {view === "public" && (
                      <CheckIcon sx={{ marginRight: "8px", width: "2rem" }} />
                    )}
                    Public
                  </ToggleButton>
                </ToggleButtonGroup>
                {view !== "public" && (
                  <Typography variant="body1" color="grey.600">
                    Only you and those you’ve added here can see your
                    collection.
                  </Typography>
                )}
              </Stack>
              {view === "public" && (
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      p: 2,
                      backgroundColor: navbarBackground,
                      borderRadius: 1,
                      mt: 1,
                    }}
                  >
                    <PublicCollectionDetailCard
                      collection={collection}
                      length={length}
                    />
                  </Box>
                </Box>
              )}
              {users.length === 0 ? (
                <Typography variant="body1" color="grey.600">
                  The Public version of your collection does not include notes,
                  summaries, citations, or bookmarks added from URLs.
                </Typography>
              ) : (
                <Typography variant="body1" color="grey.600">
                  This collection cannot be made public because others have
                  access. Remove All before switching from Private to Public.
                </Typography>
              )}
            </Stack>
          </Box>
        </Box>
      </RightSidebar>
      <PublicConfirm
        makeCollectionPublic={makeCollectionPublic}
        open={confirmPopup}
        onClose={() => setConfirmPopup(false)}
        setView={setView}
        collection={collection}
        length={length}
        view={view}
      />
      <RemoveModal
        removeState={removeEmail}
        isOpen={remove}
        onClose={() => setRemove(false)}
        onConfirm={handleDelete}
        loading={isDeleting}
        userToRemove={users.find(
          (u) =>
            u.email === removeEmail ||
            u.generatedDistributionListName === removeEmail
        )}
      />
      <PrivateConfirm
        makeCollectionPublic={makeCollectionPublic}
        isOpen={privatePopup}
        onClose={() => setPrivatePopup(false)}
        collection={collection}
        setConfirmPopup={setConfirmPopup}
        setView={setView}
      />
    </React.Fragment>
  );
};

export default AccessSidebar;
